import { DEPRECIATION_CATEGORIES, GRADE_LABELS, SEVERITY_LABELS, SIMULATOR_GRADES, SIMULATOR_SEVERITIES, } from './catalog.js';
const number = (value) => Math.round(value * 1e6) / 1e6;
const same = (a, b) => (typeof a === 'number' && typeof b === 'number' ? Math.abs(a - b) < 1e-9 : a === b);
/**
 * Every figure of `used` that differs from `defaults`, labelled for a person. Options are matched by
 * position — the lists are fixed; only the figures on them are editable.
 */
export const diffSimulatorValues = (defaults, used) => {
    const changes = [];
    const push = (path, label, unit, defaultValue, usedValue) => {
        const a = typeof defaultValue === 'number' ? number(defaultValue) : defaultValue;
        const b = typeof usedValue === 'number' ? number(usedValue) : usedValue;
        if (!same(a, b)) {
            changes.push({ path, label, unit, defaultValue: a, usedValue: b });
        }
    };
    const pctList = (key, title) => {
        defaults[key].forEach((option, index) => {
            const current = used[key][index];
            if (current) {
                push(`${key}.${index}.pct`, `${title} · ${option.name}`, '%', option.pct, current.pct);
            }
        });
    };
    for (const { key, name } of DEPRECIATION_CATEGORIES) {
        const cat = key;
        defaults.cat[cat].forEach((rate, index) => {
            push(`cat.${cat}.${index}`, `${name} ladder · Year ${index + 1}`, '%', rate, used.cat[cat][index] ?? rate);
        });
    }
    defaults.spread.forEach((share, index) => {
        push(`spread.${index}`, `Points spread · Year ${index + 1}`, 'share', share, used.spread[index] ?? share);
    });
    defaults.brand.forEach((option, index) => {
        const current = used.brand[index];
        if (current) {
            push(`brand.${index}.pts`, `Brand equity · ${option.name}`, 'pts', option.pts, current.pts);
        }
    });
    defaults.fuel.forEach((option, index) => {
        const current = used.fuel[index];
        if (current) {
            push(`fuel.${index}.pts`, `Powertrain · ${option.name}`, 'pts', option.pts, current.pts);
        }
    });
    pctList('usage', 'Usage');
    pctList('specs', 'Regional specs');
    push('warranty', 'Warranty available', '%', defaults.warranty, used.warranty);
    pctList('agency', 'Agency reputation');
    pctList('service', 'Service history');
    pctList('demand', 'Demand for the model');
    pctList('supply', 'Market supply of the model');
    for (const table of ['standard', 'luxury']) {
        for (const grade of SIMULATOR_GRADES) {
            push(`grade.${table}.${grade}`, `Chassis grade · ${table} · ${GRADE_LABELS[grade].number} ${GRADE_LABELS[grade].arabic}`, '%', defaults.grade[table][grade], used.grade[table][grade]);
        }
    }
    for (const severity of SIMULATOR_SEVERITIES) {
        push(`sev.${severity}`, `Severity · ${SEVERITY_LABELS[severity]}`, '%', defaults.sev[severity], used.sev[severity]);
    }
    push('km.allowance', 'Mileage allowance per year', 'km', defaults.km.allowance, used.km.allowance);
    push('km.step', 'Excess mileage step', 'km', defaults.km.step, used.km.step);
    push('km.rate', 'Mileage penalty per started step', '%', defaults.km.rate, used.km.rate);
    push('owners.allowance', 'Owners allowed', 'owners', defaults.owners.allowance, used.owners.allowance);
    push('owners.rate', 'Penalty per extra owner', '%', defaults.owners.rate, used.owners.rate);
    push('facelift', 'Facelift deduction', '%', defaults.facelift, used.facelift);
    push('customs', 'Customs market impact', '%', defaults.customs, used.customs);
    push('rounding', 'Rounding', 'JOD', defaults.rounding, used.rounding);
    push('worst.grade', 'Worst case · chassis grade', '', defaults.worst.grade, used.worst.grade);
    push('worst.sev', 'Worst case · all assessments', '', defaults.worst.sev, used.worst.sev);
    push('worst.service', 'Worst case · service history', '', defaults.worst.service, used.worst.service);
    push('worst.kmPerYear', 'Worst case · driven per year', 'km', defaults.worst.kmPerYear, used.worst.kmPerYear);
    return changes;
};
const trimZero = (value) => value.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
/** `0.02` with unit `%` → `2%`; `0.03` with `pts` → `3 pts`; `20000` with `km` → `20,000 km`. */
export const formatChangeValue = (value, unit) => {
    if (typeof value === 'string') {
        return value;
    }
    switch (unit) {
        case '%':
            return `${trimZero((value * 100).toFixed(2))}%`;
        case 'pts':
            return `${trimZero((value * 100).toFixed(2))} pts`;
        case 'share':
            return trimZero(value.toFixed(3));
        case 'km':
        case 'JOD':
            return `${Math.round(value).toLocaleString('en-US')} ${unit}`;
        case 'owners':
            return `${Math.round(value)} ${unit}`;
        default:
            return String(value);
    }
};
/** `Usage · Taxi or ride-hailing app: -10% → -12%`, one per change, for a cell or a sentence. */
export const describeChange = (change) => `${change.label}: ${formatChangeValue(change.defaultValue, change.unit)} → ${formatChangeValue(change.usedValue, change.unit)}`;
//# sourceMappingURL=changes.js.map